import { getLocalizedName } from "./localization";

type LocalizedField = {
  locale: string;
  field: string;
  value: string;
};

type CategoryLike = {
  _id?: string;
  name?: string;
  localized_fields?: LocalizedField[];
};

const COMBINING_MARKS_REGEX = /[\u0300-\u036f]/g;

export function normalizeCategoryValue(value?: string | null): string {
  return (value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(COMBINING_MARKS_REGEX, "");
}

function getCategoryMatchValues(
  category: CategoryLike,
  targetLocale: string,
  fallbackLocale?: string
): string[] {
  const values: string[] = [];

  if (category._id) values.push(category._id);
  if (category.name) values.push(category.name);

  const localizedName = getLocalizedName(category, targetLocale, fallbackLocale);
  if (localizedName) values.push(localizedName);

  if (fallbackLocale) {
    const fallbackName = getLocalizedName(category, fallbackLocale);
    if (fallbackName) values.push(fallbackName);
  }

  for (const field of category.localized_fields ?? []) {
    if (field.field === "name" && field.value) {
      values.push(field.value);
    }
  }

  return values;
}

export function matchesConfiguredCategory(
  category: CategoryLike,
  configuredCategory: string,
  targetLocale: string,
  fallbackLocale?: string
): boolean {
  const normalizedTarget = normalizeCategoryValue(configuredCategory);
  if (!normalizedTarget) return false;

  return getCategoryMatchValues(category, targetLocale, fallbackLocale).some(
    (value) => normalizeCategoryValue(value) === normalizedTarget
  );
}

export function hasAnySelectedCategory(
  categories: CategoryLike[],
  selectedCategoryIds: string[]
): boolean {
  const selectedSet = new Set(
    selectedCategoryIds.map((id) => normalizeCategoryValue(id)).filter(Boolean)
  );

  if (selectedSet.size === 0) return false;

  return categories.some((category) =>
    selectedSet.has(normalizeCategoryValue(category._id))
  );
}
